import React, { useEffect, useMemo, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { Reorder } from "framer-motion";
import { Wand2, Loader2, Save, Download, FileDown, Sparkles, CheckCircle2, RotateCcw, SlidersHorizontal, Route, ChevronLeft } from "lucide-react";
import api, { API } from "@/lib/api";
import ContactSection from "@/components/resume-builder/ContactSection";
import DraggableSection from "@/components/resume-builder/DraggableSection";
import { SECTION_REGISTRY, DEFAULT_SECTION_ORDER } from "@/components/resume-builder/sectionRegistry";
import AddSectionsBar from "@/components/resume-builder/AddSectionsBar";
import ResumePreview from "@/components/resume-builder/ResumePreview";
import TemplateGalleryModal from "@/components/resume-builder/TemplateGalleryModal";
import LinkedInImportDialog from "@/components/resume-builder/LinkedInImportDialog";
import VersionManager from "@/components/resume-builder/VersionManager";
import ResumeEntryGate from "@/components/resume-builder/ResumeEntryGate";
import AtsScorePanel from "@/components/resume-builder/AtsScorePanel";
import TemplateColorPicker from "@/components/resume-builder/TemplateColorPicker";
import PageSizeSelector from "@/components/resume-builder/PageSizeSelector";
import ResumeWizard from "@/components/resume-builder/ResumeWizard";
import AiToolsFab from "@/components/resume-builder/AiToolsFab";
import VideoIntroCard from "@/components/resume-builder/VideoIntroCard";
import { LAYOUTS } from "@/components/resume-builder/templates/templateConfig";

const isDraftEmpty = (d) => !d?.summary && !(d?.skills?.length) && !(d?.experience?.length) && !(d?.education?.length);

export default function ResumeBuilderPage() {
  const location = useLocation();
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoBuilding, setAutoBuilding] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [activeVersionId, setActiveVersionId] = useState(null);
  const [showGate, setShowGate] = useState(false);
  const [finalizeAtsResult, setFinalizeAtsResult] = useState(null);
  const [revealedExtras, setRevealedExtras] = useState(new Set());
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [mode, setMode] = useState("advanced"); // "wizard" (guided steps) | "advanced" (all-in-one editor)
  const [wizardStep, setWizardStep] = useState(1);

  useEffect(() => {
    const preset = location.state || {};
    api.get("/resume-builder/draft").then((res) => {
      if (preset.presetTemplate) {
        setDraft({ ...res.data, template: preset.presetTemplate, template_color: preset.presetColor || "indigo" });
        toast.success(`"${preset.presetTemplate}" template applied — start editing below`);
      } else {
        setDraft(res.data);
        if (isDraftEmpty(res.data)) setShowGate(true);
      }
      // Fresh/empty drafts default into the Guided Wizard; drafts that already have
      // content (imported, auto-built, or a returning user) default into Advanced mode.
      setMode(isDraftEmpty(res.data) ? "wizard" : "advanced");
      setRevealedExtras(new Set([
        ...(res.data.websites?.length ? ["websites"] : []),
        ...(res.data.hobbies?.length ? ["hobbies"] : []),
        ...(res.data.extracurricular?.length ? ["extracurricular"] : []),
      ]));
    }).finally(() => setLoading(false));
  }, [location.state]);

  const saveDraft = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/resume-builder/draft", draft);
      setDraft(data);
      toast.success("Draft saved");
    } catch (e) {
      toast.error("Could not save draft");
    } finally {
      setSaving(false);
    }
  };

  const autoBuildFromProfile = async () => {
    setAutoBuilding(true);
    try {
      const { data } = await api.post("/resume-builder/auto-build-profile");
      setDraft(data);
      setActiveVersionId(null);
      toast.success("Built from your Cloudivo profile — review and edit below");
    } catch (e) {
      toast.error("Could not auto-build right now");
    } finally {
      setAutoBuilding(false);
    }
  };

  const finalize = async () => {
    setFinalizing(true);
    try {
      const { data } = await api.post("/resume-builder/finalize");
      toast.success(`Resume finalized for job matching — ATS Score: ${data.ats_score}/100`);
      try {
        const { data: fullScore } = await api.post("/resume-builder/ats-score", { draft });
        setFinalizeAtsResult(fullScore);
      } catch (e) {
        // full breakdown is a nice-to-have on top of the already-successful finalize; ignore silently
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add more details before finalizing");
    } finally {
      setFinalizing(false);
    }
  };

  if (loading || !draft) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="resume-builder-loading">
        <div className="w-10 h-10 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const fullOrder = draft.section_order?.length ? draft.section_order : DEFAULT_SECTION_ORDER;
  const visibleOrderedKeys = fullOrder.filter((key) => {
    const def = SECTION_REGISTRY[key];
    if (!def) return false;
    return !def.extra || revealedExtras.has(key);
  });

  const handleReorder = (newVisibleOrder) => {
    const hiddenKeys = fullOrder.filter((k) => !visibleOrderedKeys.includes(k));
    setDraft((prev) => ({ ...prev, section_order: [...newVisibleOrder, ...hiddenKeys] }));
  };

  if (showGate) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="resume-builder-page">
        <ResumeEntryGate
          onScratch={() => { setShowGate(false); setMode("wizard"); setWizardStep(1); }}
          onImported={(structured) => { setDraft(structured); setActiveVersionId(null); setShowGate(false); setMode("advanced"); }}
        />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="resume-builder-page">
      <Link to="/dashboard" data-testid="resume-builder-back-to-dashboard-link" className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-white mb-4 transition-colors">
        <ChevronLeft className="w-3.5 h-3.5" /> Back to Dashboard
      </Link>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
        <div>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-400">Job Ready Learning</span>
          <h1 className="font-heading text-3xl font-semibold text-white mt-1">AI Resume Builder</h1>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <button
            onClick={() => setMode(mode === "wizard" ? "advanced" : "wizard")} data-testid="resume-mode-toggle-button"
            className="flex items-center gap-1.5 text-xs font-semibold text-indigo-300 hover:text-indigo-200"
          >
            {mode === "wizard" ? <SlidersHorizontal className="w-3.5 h-3.5" /> : <Route className="w-3.5 h-3.5" />}
            {mode === "wizard" ? "Switch to Advanced Mode" : "Switch to Guided Mode"}
          </button>
          <button onClick={() => setShowGate(true)} data-testid="resume-start-new-button" className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-white">
            <RotateCcw className="w-3.5 h-3.5" /> Start New Resume
          </button>
          <VersionManager draft={draft} activeVersionId={activeVersionId} setActiveVersionId={setActiveVersionId} onVersionLoaded={setDraft} />
          <LinkedInImportDialog onImported={(d) => { setDraft(d); setActiveVersionId(null); }} />
        </div>
      </div>
      <p className="text-slate-400 text-sm mb-6">
        {mode === "wizard"
          ? "A guided, step-by-step flow — Basics, Experience, Skills, AI Tools, then Review & Download."
          : "Build a section-by-section resume with AI writing help, or auto-build it from your real Cloudivo activity."}
      </p>

      {mode === "advanced" && (
        <button onClick={autoBuildFromProfile} disabled={autoBuilding} data-testid="resume-auto-build-profile-button" className="flex items-center gap-2 bg-indigo-500 hover:bg-indigo-400 disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors mb-6">
          {autoBuilding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Auto-Build from My Cloudivo Profile
        </button>
      )}

      {mode === "wizard" ? (
        <ResumeWizard
          draft={draft} setDraft={setDraft}
          revealedExtras={revealedExtras} setRevealedExtras={setRevealedExtras}
          onBrowseAll={() => setGalleryOpen(true)}
          step={wizardStep} setStep={setWizardStep}
          saving={saving} saveDraft={saveDraft}
          finalizing={finalizing} finalize={finalize} finalizeAtsResult={finalizeAtsResult}
          setActiveVersionId={setActiveVersionId}
        />
      ) : (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4" data-testid="resume-builder-editor">
          <ContactSection draft={draft} setDraft={setDraft} />
          <p className="text-[11px] text-slate-500 flex items-center gap-1.5 -mb-1 lg:pl-5" data-testid="resume-drag-hint">
            Drag the grip handle on the left of any section below to reorder it
          </p>
          <Reorder.Group as="div" axis="y" values={visibleOrderedKeys} onReorder={handleReorder} className="space-y-4">
            {visibleOrderedKeys.map((key) => (
              <DraggableSection key={key} sectionKey={key} label={SECTION_REGISTRY[key].label}>
                {SECTION_REGISTRY[key].render({ draft, setDraft })}
              </DraggableSection>
            ))}
          </Reorder.Group>
          <AddSectionsBar draft={draft} setDraft={setDraft} revealed={revealedExtras} onReveal={(key) => setRevealedExtras((prev) => new Set(prev).add(key))} />
        </div>

        <div className="lg:sticky lg:top-6 self-start space-y-4">
          <TemplateColorPicker draft={draft} setDraft={setDraft} onBrowseAll={() => setGalleryOpen(true)} />
          <PageSizeSelector draft={draft} setDraft={setDraft} />
          <VideoIntroCard draft={draft} setDraft={setDraft} />

          <ResumePreview draft={draft} />

          <AtsScorePanel draft={draft} setDraft={setDraft} presetResult={finalizeAtsResult} />

          <div className="glass-panel rounded-2xl p-4 flex flex-wrap items-center gap-3">
            <button onClick={saveDraft} disabled={saving} data-testid="resume-save-draft-button" className="flex items-center gap-1.5 bg-white/10 hover:bg-white/15 disabled:opacity-60 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save Draft
            </button>
            <a href={`${API}/resume-builder/pdf?template=${draft.template}&template_color=${draft.template_color}&page_size=${draft.page_size || "A4"}`} target="_blank" rel="noopener noreferrer" data-testid="resume-download-pdf-button" className="flex items-center gap-1.5 bg-indigo-500 hover:bg-indigo-400 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors">
              <Download className="w-3.5 h-3.5" /> Download PDF
            </a>
            <a href={`${API}/resume-builder/docx?template_color=${draft.template_color}&page_size=${draft.page_size || "A4"}`} target="_blank" rel="noopener noreferrer" data-testid="resume-download-docx-button" className="flex items-center gap-1.5 bg-white/10 hover:bg-white/15 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors">
              <FileDown className="w-3.5 h-3.5" /> Download Word
            </a>
            <button onClick={finalize} disabled={finalizing} data-testid="resume-finalize-button" className="flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors">
              {finalizing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Finalize & Use for Job Matching
            </button>
          </div>
        </div>
      </div>
      )}

      {galleryOpen && (
        <TemplateGalleryModal
          draft={draft}
          onClose={() => setGalleryOpen(false)}
          onSelect={(templateId) => {
            setDraft({ ...draft, template: templateId });
            toast.success(`"${LAYOUTS.find((t) => t.id === templateId)?.label}" template selected`);
            setGalleryOpen(false);
          }}
        />
      )}

      <AiToolsFab
        draft={draft} setDraft={setDraft} setActiveVersionId={setActiveVersionId} mode={mode}
        onJumpToWizardStep={() => setWizardStep(4)}
      />
    </div>
  );
}
